import {Trans} from '@lingui/macro';
import {flow, range} from 'lodash';
import memoize from 'lru-memoize';
import memoizeOne from 'memoize-one';
import {transparentize} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {basicMarkdownStyle} from '../../../../libs/shared/ui/src/styles/markdownStyles';
import {ellipsisAfterNumberOfLines} from '../../../../libs/shared/ui/src/styles/mixins';
import {MarkdownFormatter} from '../../../../srcCommon/formatters/markdown/markdownFormatter';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {AnnotationHighlightCommentModel} from '../../../models/api/annotationModel';
import {
  AttachmentModel,
  AttachmentStatusesEnum,
  AttachmentVisualContentTypesEnum,
  SimpleAttachmentModel,
  UploadedAttachmentModel,
} from '../../../models/api/attachmentModel';
import {CommentModel} from '../../../models/api/commentModel';
import {EmojiMap} from '../../../services/emojis/emojis';
import {getEmojiValidationMap} from '../../../store/stateless/emojiSelectors';
import {MarkdownTokenFormatter} from '../formatters/markdown/markdownTokenFormatter';
import {makeMarkdownTransformEmojis} from '../formatters/markdown/markdownTransformEmojis';
import {makeMarkdownTransformMentions} from '../formatters/markdown/markdownTransformMentions';
import {useLinkNavigation} from '../navigation/useLinkNavigation';
import {CommentAttachment} from './commentAttachment';

/*
 * Props.
 */

interface CommentSummaryProps {
  comment: CommentModel | undefined;
  shouldShowAttachments?: boolean;
  isJumpPointerDisabled?: boolean;
  hasQuoteComment: boolean;
  aliases: ReadonlySet<string>;
  annotation?: AnnotationHighlightCommentModel;
  shouldTruncateAnnotationText?: boolean;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  margin: 4px 4px 4px 0px;

  &.jump-comment-summary {
    cursor: pointer;
  }

  &.jump-comment-summary:hover * {
    cursor: pointer;
  }

  &.jump-comment-summary:hover .reply-div p {
    color: ${(p) => p.theme.greys.shade80};
  }

  &.jump-comment-summary:hover .annotation-div {
    background-color: ${transparentize(0.7, '#FF5C00')};
  }

  &.jump-comment-summary:hover .original-edited-indicator {
    color: ${(p) => p.theme.greys.shade90};
  }

  &.jump-comment-summary .original-edited-indicator,
  &.comment-summary .original-edited-indicator {
    margin-left: 4px;
  }
`;

const StyledQuoteDiv = styled.div`
  align-self: stretch;
  width: 0px;
  border: 1px solid ${(p) => p.theme.greys.shade60};
  border-radius: 1px;
  margin: 2px 6px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const makeOrderedListStyle = memoizeOne(() => {
  const withoutStart = `
    ol {
      counter-reset: list-counter;

      li {
        &::before {
          content: ' ' counter(list-counter) '. ';
          counter-increment: list-counter;
        }
      }
    }
  `;

  const withStart = range(100)
    .map(
      (i) => `
      ol[start="${i}"] {
        counter-reset: list-counter ${i - 1};
    
        li {
          &::before {
            content: ' ' counter(list-counter) '. ';
            counter-increment: list-counter;
          }
        }
      }
    `,
    )
    .join('');

  return `
    ${withoutStart}
    ${withStart}
  `;
});

const StyledMarkdownDiv = styled.div`
  ${basicMarkdownStyle()};

  & {
    white-space: normal;
  }

  * {
    display: inline;
    word-wrap: break-word;
  }

  p {
    margin: 0;
  }

  blockquote {
    margin: 0.5em 4px;

    &::before {
      content: '“';
    }

    &::after {
      content: '”';
    }
  }

  ol,
  ul {
    margin: 0;
    padding-left: 1.5em;
    padding: 0;
  }

  ${makeOrderedListStyle()}

  li {
    margin: 0;
    &::before {
      content: ' ';
    }
    &::after {
      content: ' ';
    }
  }

  code {
    padding: 1px;
    margin: -1px 0;
  }

  pre {
    max-width: 80ch;
    padding: 0.5em 0;
    white-space: normal;

    code {
      display: inline;
      padding: 2px 2px;
      margin: 0px 4px;
    }
  }

  img.front-markdown-image {
    max-width: 200px;
    max-height: 200px;
    border-radius: 10px;
  }
`;

const StyledLegacyReplyContainerDiv = styled(StyledMarkdownDiv)`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.tiny};
  line-height: 16px;
  ${ellipsisAfterNumberOfLines(2)}
`;

const StyledSummaryTextContainerDiv = styled(StyledMarkdownDiv)`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  line-height: 17px;
`;

const StyledTruncatedSummaryTextContainerDiv = styled(StyledSummaryTextContainerDiv)`
  display: flex;
`;

const StyledReplyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  line-height: 17px;
  padding: 0 2px;
  ${ellipsisAfterNumberOfLines(2)}
`;

const StyledHighlightCommentAnnotationDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  line-height: 17px;
  background-color: ${transparentize(0.8, '#FF5C00')};
  padding: 0 2px;
`;

const StyledTruncatedHighlightCommentAnnotationDiv = styled(StyledHighlightCommentAnnotationDiv)`
  ${ellipsisAfterNumberOfLines(1)}
`;

const StyledOriginalEditedSpan = styled.span`
  font-style: italic;
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  white-space: nowrap;
`;

const StyledTombstoneSpan = styled.span`
  font-style: italic;
`;

const StyledQuoteCommentTombstoneSpan = styled.span`
  font-style: italic;
  font-size: ${fontSizes.verySmall};
`;

/*
 * Component.
 */

export const CommentSummary: FC<CommentSummaryProps> = (props) => {
  const {comment, annotation, hasQuoteComment, isJumpPointerDisabled} = props;
  const {navigateToLink, maybeRenderLinkError} = useLinkNavigation();
  const isTombstone = !comment;
  const hasAnnotation = Boolean(annotation?.text);
  const emojiValidationMap = useAppSelector(getEmojiValidationMap);

  let quoteCommentCssClassName;
  if (isTombstone) {
    quoteCommentCssClassName = 'comment-tombstone';
  } else if (!isJumpPointerDisabled) {
    quoteCommentCssClassName = 'jump-comment-summary';
  } else {
    quoteCommentCssClassName = 'comment-summary';
  }

  const styledContainerDivCssClassName = hasQuoteComment ? quoteCommentCssClassName : '';

  return (
    <StyledContainerDiv className={styledContainerDivCssClassName}>
      <StyledQuoteDiv />
      <StyledContentDiv>
        {renderContent(props, isTombstone, hasAnnotation, navigateToLink, emojiValidationMap)}
        {maybeRenderLinkError()}
      </StyledContentDiv>
    </StyledContainerDiv>
  );
};

/*
 * Helpers.
 */

function renderContent(
  props: CommentSummaryProps,
  isTombstone: boolean,
  hasAnnotation: boolean,
  onLinkClick: (link: string, shiftKey?: boolean) => void,
  emojiValidationMap: EmojiMap,
) {
  const {hasQuoteComment, annotation, shouldTruncateAnnotationText} = props;

  const {visualizedAttachment, summarizedAttachments} = partitionAttachments(props, hasAnnotation);

  if (visualizedAttachment !== null) {
    return (
      <>
        <CommentAttachment attachment={visualizedAttachment} />
        {renderText(
          hasQuoteComment,
          isTombstone,
          hasAnnotation,
          props.comment,
          annotation,
          props.aliases,
          onLinkClick,
          emojiValidationMap,
          summarizedAttachments,
          shouldTruncateAnnotationText,
        )}
      </>
    );
  }

  return renderText(
    hasQuoteComment,
    isTombstone,
    hasAnnotation,
    props.comment,
    annotation,
    props.aliases,
    onLinkClick,
    emojiValidationMap,
    summarizedAttachments,
    shouldTruncateAnnotationText,
  );
}

function renderText(
  hasQuoteComment: boolean,
  isTombstone: boolean,
  hasAnnotation: boolean,
  comment: CommentModel | undefined,
  annotation: AnnotationHighlightCommentModel | undefined,
  aliases: ReadonlySet<string>,
  onLinkClick: (link: string, shiftKey?: boolean) => void,
  emojiValidationMap: EmojiMap,
  summarizedAttachments: (AttachmentModel | SimpleAttachmentModel)[],
  shouldTruncateAnnotationText?: boolean,
) {
  const summaryText = [
    annotation?.text || comment?.text || '',
    attachmentsAsText(summarizedAttachments),
  ].join(' ');

  // We're done if we've rendered an attachment and the comment doesn't have any text.
  if (!summaryText) {
    return null;
  }

  if (hasQuoteComment) {
    const originalWasEdited =
      comment?.lastEditedAt !== undefined &&
      annotation?.createdAt !== undefined &&
      comment.lastEditedAt >= annotation.createdAt;

    const SummaryTextContainerDiv =
      shouldTruncateAnnotationText && hasAnnotation
        ? StyledTruncatedSummaryTextContainerDiv
        : StyledSummaryTextContainerDiv;

    return (
      <SummaryTextContainerDiv>
        {!isTombstone &&
          renderSummaryText(
            summaryText,
            hasAnnotation,
            aliases,
            onLinkClick,
            emojiValidationMap,
            shouldTruncateAnnotationText,
          )}
        {!isTombstone && originalWasEdited && (
          <StyledOriginalEditedSpan className="original-edited-indicator">
            <Trans id="original-comment-edited">(original edited)</Trans>
          </StyledOriginalEditedSpan>
        )}
        {isTombstone && renderTombstone(hasQuoteComment)}
      </SummaryTextContainerDiv>
    );
  }

  return (
    <StyledLegacyReplyContainerDiv>
      {isTombstone
        ? renderTombstone(hasQuoteComment)
        : renderMarkdown(summaryText, aliases, onLinkClick, emojiValidationMap)}
    </StyledLegacyReplyContainerDiv>
  );
}

function renderSummaryText(
  summaryText: string,
  hasAnnotation: boolean,
  aliases: ReadonlySet<string>,
  onLinkClick: (link: string, shiftKey?: boolean) => void,
  emojiValidationMap: EmojiMap,
  shouldTruncateAnnotationText?: boolean,
) {
  const markdown = renderMarkdown(summaryText, aliases, onLinkClick, emojiValidationMap);

  if (hasAnnotation && shouldTruncateAnnotationText) {
    return (
      <StyledTruncatedHighlightCommentAnnotationDiv className="annotation-div">
        {markdown}
      </StyledTruncatedHighlightCommentAnnotationDiv>
    );
  }
  if (hasAnnotation) {
    return (
      <StyledHighlightCommentAnnotationDiv className="annotation-div">
        {markdown}
      </StyledHighlightCommentAnnotationDiv>
    );
  }

  return <StyledReplyDiv className="reply-div">{markdown}</StyledReplyDiv>;
}

function renderMarkdown(
  summaryText: string,
  aliases: ReadonlySet<string>,
  onLinkClick: (link: string, shiftKey?: boolean) => void,
  emojiValidationMap: EmojiMap,
) {
  return (
    <MarkdownFormatter
      formatter={MarkdownTokenFormatter}
      text={summaryText}
      transform={makeMarkdownTransforms(aliases, emojiValidationMap)}
      onLinkClick={onLinkClick}
    />
  );
}

function renderTombstone(hasQuoteComment: boolean) {
  const TombstoneSpan = hasQuoteComment ? StyledQuoteCommentTombstoneSpan : StyledTombstoneSpan;
  return (
    <TombstoneSpan>
      <Trans id="comment-summary-deleted">This comment was deleted</Trans>
    </TombstoneSpan>
  );
}

function partitionAttachments(
  props: CommentSummaryProps,
  hasAnnotation: boolean,
): {
  visualizedAttachment: UploadedAttachmentModel | null;
  summarizedAttachments: (AttachmentModel | SimpleAttachmentModel)[];
} {
  const {comment} = props;
  const attachments = comment?.attachments ?? [];
  const visualizedAttachment = findVisualizedAttachment(props);

  if (hasAnnotation) {
    return {
      visualizedAttachment: null,
      summarizedAttachments: [],
    };
  }

  return {
    visualizedAttachment,
    summarizedAttachments: attachments.filter(
      (attachment) =>
        visualizedAttachment === null ||
        !('uid' in attachment) || // is SimpleAttachment
        attachment.uid !== visualizedAttachment.uid,
    ),
  };
}

function findVisualizedAttachment(props: CommentSummaryProps): UploadedAttachmentModel | null {
  if (!props.comment) {
    // There is no comment.
    return null;
  }

  if (!props.shouldShowAttachments) {
    // We shouldn't render any attachments.
    return null;
  }

  if (props.comment.attachments.length !== 1) {
    // There are zero or multiple attachments.
    return null;
  }

  const attachment = props.comment.attachments[0];
  if (
    !('visualContentType' in attachment) ||
    !('status' in attachment) ||
    attachment.status !== AttachmentStatusesEnum.UPLOADED ||
    attachment.visualContentType !== AttachmentVisualContentTypesEnum.IMAGE
  ) {
    // The attachment is not uploaded or not an image.
    return null;
  }

  return attachment;
}

function attachmentsAsText(attachments: (AttachmentModel | SimpleAttachmentModel)[]) {
  return attachments
    .filter((attachment) => 'name' in attachment)
    .map((attachment) => `[${attachment.name}]`)
    .join(' ');
}

const makeMarkdownTransforms = memoize()(
  (aliases: ReadonlySet<string>, validationMap: EmojiMap) => (node: ReactNode) =>
    flow(makeMarkdownTransformMentions(aliases), makeMarkdownTransformEmojis(validationMap))(node),
);
